import { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';

const baseStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: '#FAFAFA',
  border: '1.5px solid #F0C8D8',
  borderRadius: '12px',
  padding: '13px 16px',
  fontSize: '14px',
  color: '#1A0A10',
  outline: 'none',
  transition: 'border-color 0.2s, background 0.2s',
  fontFamily: 'Inter, sans-serif',
};

export const FieldLabel = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: 'block',
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#6B3A52',
      marginBottom: '8px',
    }}
  >
    {children}
  </label>
);

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ style, onFocus, onBlur, ...props }, ref) {
    return (
      <input
        ref={ref}
        {...props}
        style={{ ...baseStyle, ...style }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#C2185B';
          e.currentTarget.style.background = '#fff';
          onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#F0C8D8';
          e.currentTarget.style.background = '#FAFAFA';
          onBlur?.(e);
        }}
      />
    );
  },
);

export const TextTextarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextTextarea({ style, onFocus, onBlur, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        {...props}
        style={{ ...baseStyle, minHeight: 120, resize: 'vertical', ...style }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#C2185B';
          e.currentTarget.style.background = '#fff';
          onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#F0C8D8';
          e.currentTarget.style.background = '#FAFAFA';
          onBlur?.(e);
        }}
      />
    );
  },
);

export function FormButton({
  children,
  loading,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
        padding: '14px 22px',
        fontSize: 15,
        fontWeight: 700,
        color: 'white',
        border: 'none',
        borderRadius: 999,
        cursor: loading ? 'wait' : 'pointer',
        background: 'linear-gradient(135deg, #C2185B, #E91E63)',
        boxShadow: '0 8px 28px rgba(233,30,99,0.35)',
        opacity: loading || props.disabled ? 0.7 : 1,
        fontFamily: 'Inter, sans-serif',
        ...((props.style as React.CSSProperties) || {}),
      }}
    >
      {loading ? 'Submitting…' : children}
    </button>
  );
}

/** Visually hidden honeypot — leave empty */
export function HoneypotInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    // Name and label must not resemble real fields (company, website, url…), or browser autofill fills it
    // and every genuine submission is silently dropped as a bot.
    <div className="hp-field" aria-hidden="true">
      <label htmlFor="mz_hp_ref">Leave this empty</label>
      <input
        id="mz_hp_ref"
        name="mz_hp_ref"
        type="text"
        tabIndex={-1}
        autoComplete="new-password"
        data-lpignore="true"
        data-1p-ignore="true"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
